//! Converts a chart's cell anchor into a pixel rect using the client-side
//! sheet offsets (which may be transient while a column/row resize is being
//! dragged). Mirrors core's `ChartAnchor::pixel_rect` plus the `editAs`
//! fixup semantics of `Sheet::charts_after_offsets_change`.

import type { Sheet } from '@/app/grid/sheet/Sheet';
import type { CellAnchorPoint, JsChart } from '@/app/quadratic-core-types';
import { Rectangle } from 'pixi.js';

/** EMUs (English Metric Units) per pixel at 96 DPI (mirrors core's `EMU_PER_PIXEL`). */
const EMU_PER_PIXEL = 9525;

const anchorPointToPixels = (point: CellAnchorPoint, sheet: Sheet): { x: number; y: number } => {
  const col = Math.max(Number(point.col), 1);
  const row = Math.max(Number(point.row), 1);
  const cell = sheet.getCellOffsets(col, row);
  return {
    x: cell.x + Number(point.col_offset_emu) / EMU_PER_PIXEL,
    y: cell.y + Number(point.row_offset_emu) / EMU_PER_PIXEL,
  };
};

/**
 * The pixel rect a chart occupies given the sheet's current offsets. Used to
 * keep charts anchored while column/row resizes change the offsets, before
 * core re-sends the authoritative layout. Follows Excel's `editAs` semantics:
 * two-cell anchors move and size with cells, `OneCell`-edited anchors move
 * but keep their pixel size, and `Absolute` ones don't move at all.
 */
export const chartAnchorRect = (chart: JsChart, sheet: Sheet): Rectangle => {
  const anchor = chart.anchor;
  if ('TwoCell' in anchor) {
    const { from, to, edit_as } = anchor.TwoCell;
    if (edit_as === 'Absolute') {
      return new Rectangle(chart.x, chart.y, chart.w, chart.h);
    }
    const min = anchorPointToPixels(from, sheet);
    if (edit_as === 'OneCell') {
      return new Rectangle(min.x, min.y, chart.w, chart.h);
    }
    const max = anchorPointToPixels(to, sheet);
    return new Rectangle(min.x, min.y, Math.max(max.x - min.x, 0), Math.max(max.y - min.y, 0));
  }
  if ('OneCell' in anchor) {
    const { from, width_emu, height_emu } = anchor.OneCell;
    const min = anchorPointToPixels(from, sheet);
    return new Rectangle(min.x, min.y, Number(width_emu) / EMU_PER_PIXEL, Number(height_emu) / EMU_PER_PIXEL);
  }
  const { x_emu, y_emu, width_emu, height_emu } = anchor.Absolute;
  return new Rectangle(
    Number(x_emu) / EMU_PER_PIXEL,
    Number(y_emu) / EMU_PER_PIXEL,
    Number(width_emu) / EMU_PER_PIXEL,
    Number(height_emu) / EMU_PER_PIXEL
  );
};
